import { useQueries } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

type StoredToken = { AccessToken?: string };

export type PaymentReportingFilters = {
  start_date: string;
  end_date: string;
};

export type KpiValue = {
  value: number;
  percent_change: number;
};

// `children` drives the row's expand chevron — present = nested, absent = flat row.
export type ProviderRow = {
  key: string;
  provider: string;
  deposits: number;
  amount: number;
  acceptance_rate: number;
  children?: ProviderRow[];
};

export type PaymentReportingKpis = {
  total_amount: KpiValue;
  total_count: KpiValue;
  acceptance_rate: KpiValue;
};

export type PaymentReportingResponse = {
  deposits: {
    kpis: PaymentReportingKpis;
    providers: ProviderRow[];
  };
  withdrawals: {
    kpis: PaymentReportingKpis;
  };
};

export type ExportRow = {
  section: "Deposits" | "Withdrawals";
  provider: string;
  count: number;
  amount: number;
  acceptance_rate_pct: number;
};

type BackendKpiMetric = {
  value: number;
  previous_value: number;
  percent_change: number | null;
};

type BackendDirectionKpis = {
  total_amount: BackendKpiMetric;
  total_count: BackendKpiMetric;
  acceptance_rate: BackendKpiMetric;
};

type BackendKpisResponse = {
  deposits: BackendDirectionKpis;
  withdrawals: BackendDirectionKpis;
};

type BackendDepositRow = {
  provider: string;
  deposit_amount: number;
  number_of_deposits: number;
  total_attempts: number;
  acceptance_rate: number;
};

// Backend returns acceptance rate `value` as a 0..1 ratio; the UI expects a percent.
// `percent_change` already comes in percent, no scaling needed.
const ratioToPercent = (ratio: number): number =>
  Number((ratio * 100).toFixed(2));

const toKpi = (metric: BackendKpiMetric): KpiValue => ({
  value: metric.value,
  percent_change: metric.percent_change ?? 0,
});

const getAdminPanelInstance = () => {
  const tokenString = localStorage.getItem("token");
  let token: StoredToken | null = null;
  try {
    token = tokenString ? JSON.parse(tokenString) : null;
  } catch {
    token = null;
  }
  return createPrivateAdminPanelInstance(token?.AccessToken);
};

const fetchKpis = async (
  filters: PaymentReportingFilters,
): Promise<BackendKpisResponse> => {
  const instance = getAdminPanelInstance();
  const response = await instance.get(API_ROUTES.PAYMENT_REPORTING_KPIS, {
    params: filters,
  });
  return response?.data?.data as BackendKpisResponse;
};

const fetchDepositsSummary = async (
  filters: PaymentReportingFilters,
): Promise<BackendDepositRow[]> => {
  const instance = getAdminPanelInstance();
  const response = await instance.get(
    API_ROUTES.PAYMENT_REPORTING_DEPOSITS_SUMMARY,
    { params: filters },
  );
  return (response?.data?.data ?? []) as BackendDepositRow[];
};

const adaptKpis = (kpis: BackendDirectionKpis): PaymentReportingKpis => ({
  total_amount: toKpi(kpis.total_amount),
  total_count: toKpi(kpis.total_count),
  acceptance_rate: {
    value: ratioToPercent(kpis.acceptance_rate.value),
    percent_change: kpis.acceptance_rate.percent_change ?? 0,
  },
});

const adaptProviders = (rows: BackendDepositRow[]): ProviderRow[] =>
  rows.map((row) => ({
    key: row.provider,
    provider: row.provider,
    deposits: row.number_of_deposits,
    amount: row.deposit_amount,
    acceptance_rate: ratioToPercent(row.acceptance_rate),
  }));

const adaptResponse = (
  kpis: BackendKpisResponse,
  deposits: BackendDepositRow[],
): PaymentReportingResponse => ({
  deposits: {
    kpis: adaptKpis(kpis.deposits),
    providers: adaptProviders(deposits),
  },
  withdrawals: {
    kpis: adaptKpis(kpis.withdrawals),
  },
});

export const usePaymentReporting = (filters: PaymentReportingFilters) => {
  const { start_date, end_date } = filters;
  const enabled = Boolean(start_date && end_date);

  const [kpisQuery, depositsQuery] = useQueries({
    queries: [
      {
        queryKey: ["PAYMENT_REPORTING_KPIS", start_date, end_date],
        queryFn: () => fetchKpis(filters),
        enabled,
        staleTime: 1000 * 60,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ["PAYMENT_REPORTING_DEPOSITS", start_date, end_date],
        queryFn: () => fetchDepositsSummary(filters),
        enabled,
        staleTime: 1000 * 60,
        refetchOnWindowFocus: false,
      },
    ],
  });

  const data =
    kpisQuery.data && depositsQuery.data
      ? adaptResponse(kpisQuery.data, depositsQuery.data)
      : undefined;

  return {
    data,
    isFetching: kpisQuery.isFetching || depositsQuery.isFetching,
    isLoading: kpisQuery.isLoading || depositsQuery.isLoading,
    isSuccess: kpisQuery.isSuccess && depositsQuery.isSuccess,
    isError: kpisQuery.isError || depositsQuery.isError,
    error: kpisQuery.error ?? depositsQuery.error,
  };
};
